/**
 * Staff roles that are DERIVED from a user's attributes instead of assigned by hand.
 *
 * Today that is the `Agency Principal` Descope role (#35833): a staff user whose `Agency Role`
 * field (Cognito `custom:agency_role` / Descope `agencyRole`) contains "Agency Principal" holds the
 * role, everyone else does not. The role grants the `IsAgencyPrincipal` and `CanViewAgencyReports`
 * permissions (Cognito groups declared in staff-appsync-be/template.yaml; the CreateDescopeRole
 * lambda mirrors every Cognito group into a Descope permission). `CanManageSweepAccount` joins the
 * role in the Descope console at the end of the sweep-account rollout (#32606), which is why
 * `ensureAgencyPrincipalRoleExists` only ever CREATES the role and never rewrites its permissions.
 *
 * Two code paths keep Descope (the source of truth for staff users) in line with the rule, and
 * both import from here so they cannot drift — the `Unlicensed` precedent (#34698) is duplicated
 * between tools/staff-users/helpers.ts and the sync lambda's helpers.js, and this package exists
 * so the next derived role doesn't repeat that:
 *   - staff-appsync-be/lambdas/sync-descope-user-to-cognito: every login
 *   - staff-appsync-be/applications/tools/staff-users: create / update staff user
 * Hand-assigning the role or `IsAgencyPrincipal` is refused (`rejectDerivedAgencyPrincipalGroup`)
 * because the next login would undo it anyway, except that Add User To Group on a user whose Agency
 * Role already qualifies re-syncs the role instead (#36599); `CanViewAgencyReports` stays directly grantable.
 *
 * Where the role is added: a Descope user's roles live either on their first tenant or at the user
 * level (legacy). The login sync reads tenant roles when there are any and user-level roles
 * otherwise, so the derived role must land on the level that is in effect — adding a tenant role
 * to a user whose roles are user-level would make the sync drop every other permission they have.
 * Same fallback as tools/staff-users add-user-to-group.
 *
 * No dependency on @descope/node-sdk: callers pass their own client and only the handful of
 * management calls used here are typed (structurally).
 */

export const AGENCY_PRINCIPAL_ROLE = 'Agency Principal';
export const IS_AGENCY_PRINCIPAL_GROUP = 'IsAgencyPrincipal';
export const CAN_VIEW_AGENCY_REPORTS_GROUP = 'CanViewAgencyReports';
export const AGENCY_PRINCIPAL_ROLE_PERMISSIONS: readonly string[] = [
  IS_AGENCY_PRINCIPAL_GROUP,
  CAN_VIEW_AGENCY_REPORTS_GROUP
];
export const AGENCY_PRINCIPAL_ROLE_DESCRIPTION =
  'Derived from the Agency Role field, never assigned by hand (#35833). Grants agency-principal-only features; add CanManageSweepAccount here at sweep GA.';

const PRINCIPAL_AGENCY_ROLE_MARKER = 'agency principal';

/**
 * The rule. Matches every current lookup value — `Agency Principal`, `- Producing`, `- Non-producing`
 * (packages/lookups agencyRoles) — and any future variant that keeps the words.
 */
export function checkIfAgencyRoleIsPrincipal(agencyRole: unknown): boolean {
  return typeof agencyRole === 'string' && agencyRole.trim().toLowerCase().includes(PRINCIPAL_AGENCY_ROLE_MARKER);
}

/** The role and the permission that only ever come from the rule. */
export function isDerivedAgencyPrincipalGroup(group: string): boolean {
  return group === IS_AGENCY_PRINCIPAL_GROUP || group === AGENCY_PRINCIPAL_ROLE;
}

/** Assigning or removing the derived role by hand is undone at the next login, so refuse it with guidance. */
export function rejectDerivedAgencyPrincipalGroup(group: string, action: 'add' | 'remove'): void {
  if (!isDerivedAgencyPrincipalGroup(group)) {
    return;
  }
  throw new Error(
    action === 'add'
      ? `'${group}' cannot be assigned directly: it is derived from the Agency Role field. Set the user's Agency Role to an Agency Principal value instead.`
      : `'${group}' cannot be removed directly: it is derived from the Agency Role field. To revoke it, change the user's Agency Role away from Agency Principal.`
  );
}

/** Role list with the derived role present or absent as the rule dictates (order preserved, no duplicates). */
export function withAgencyPrincipalRole(roleNames: string[], shouldHavePrincipal: boolean): string[] {
  const without = roleNames.filter((name) => name !== AGENCY_PRINCIPAL_ROLE);
  return shouldHavePrincipal ? [...without, AGENCY_PRINCIPAL_ROLE] : without;
}

/**
 * Permission list corrected for the rule. A principal always ends up with both role permissions
 * (so one login is right even if the Descope role change above failed); a non-principal loses only
 * `IsAgencyPrincipal` — `CanViewAgencyReports` can be granted directly. The role NAME is never a
 * permission, so it is dropped if a legacy user-level path let it through.
 */
export function withAgencyPrincipalPermissions(permissions: string[], shouldHavePrincipal: boolean): string[] {
  const base = permissions.filter(
    (p) => p !== AGENCY_PRINCIPAL_ROLE && (shouldHavePrincipal || p !== IS_AGENCY_PRINCIPAL_GROUP)
  );
  if (!shouldHavePrincipal) {
    return base;
  }
  return [...base, ...AGENCY_PRINCIPAL_ROLE_PERMISSIONS.filter((p) => !base.includes(p))];
}

type DescopeResponse<T = unknown> = { ok: boolean; data?: T; error?: unknown };
type DescopeRole = { name: string; permissionNames?: string[] };

/** The slice of @descope/node-sdk's management API this package drives. */
export type DescopeRoleManagementClient = {
  management: {
    role: {
      search: (options: { roleNames?: string[] }) => Promise<DescopeResponse<DescopeRole[]>>;
      create: (name: string, description?: string, permissionNames?: string[]) => Promise<DescopeResponse>;
    };
    user: {
      addTenantRoles: (loginId: string, tenantId: string, roles: string[]) => Promise<DescopeResponse>;
      removeTenantRoles: (loginId: string, tenantId: string, roles: string[]) => Promise<DescopeResponse>;
      addRoles: (loginId: string, roles: string[]) => Promise<DescopeResponse>;
      removeRoles: (loginId: string, roles: string[]) => Promise<DescopeResponse>;
    };
  };
};

export type DescopeUserRoles = {
  loginIds?: string[];
  roleNames?: string[];
  userTenants?: { tenantId?: string; roleNames?: string[] }[];
};

// Once a process has seen the role in Descope it stays seen: the sync lambda runs on every staff
// login and a role.search per login would be pure waste. A deleted role surfaces as a failed
// addTenantRoles, which callers log; the next cold start re-checks.
let agencyPrincipalRoleConfirmed = false;

/** Test hook. */
export function resetAgencyPrincipalRoleCache(): void {
  agencyPrincipalRoleConfirmed = false;
}

/**
 * Create-on-miss for the `Agency Principal` role so every environment self-heals after the deploy
 * that adds the Cognito groups (and so their Descope permissions). Never touches an existing role.
 */
export async function ensureAgencyPrincipalRoleExists(
  descopeClient: DescopeRoleManagementClient
): Promise<{ created: boolean }> {
  if (agencyPrincipalRoleConfirmed) {
    return { created: false };
  }
  const searchRes = await descopeClient.management.role.search({ roleNames: [AGENCY_PRINCIPAL_ROLE] });
  if (!searchRes.ok) {
    throw new Error(`Descope role search failed for "${AGENCY_PRINCIPAL_ROLE}": ${JSON.stringify(searchRes.error)}`);
  }
  if ((searchRes.data ?? []).some((role) => role.name === AGENCY_PRINCIPAL_ROLE)) {
    agencyPrincipalRoleConfirmed = true;
    return { created: false };
  }
  const createRes = await descopeClient.management.role.create(
    AGENCY_PRINCIPAL_ROLE,
    AGENCY_PRINCIPAL_ROLE_DESCRIPTION,
    [...AGENCY_PRINCIPAL_ROLE_PERMISSIONS]
  );
  if (!createRes.ok) {
    throw new Error(
      `Failed to create the "${AGENCY_PRINCIPAL_ROLE}" role in Descope (do the ${AGENCY_PRINCIPAL_ROLE_PERMISSIONS.join(', ')} permissions exist yet?): ${JSON.stringify(createRes.error)}`
    );
  }
  agencyPrincipalRoleConfirmed = true;
  return { created: true };
}

/**
 * Add or remove the `Agency Principal` role on the Descope user when it disagrees with the rule.
 * Adds on the level the user's roles are read from (see file header); removes from wherever it is.
 * Throws when Descope refuses, so the caller decides whether that fails the operation (tools) or is
 * logged and tolerated (login sync).
 */
export async function enforceAgencyPrincipalRole({
  descopeClient,
  descopeUser,
  shouldHavePrincipal
}: {
  descopeClient: DescopeRoleManagementClient;
  descopeUser: DescopeUserRoles;
  shouldHavePrincipal: boolean;
}): Promise<{ changed: boolean; level?: 'tenant' | 'user' }> {
  const loginId = descopeUser.loginIds?.[0];
  if (!loginId) {
    return { changed: false };
  }
  const tenant = descopeUser.userTenants?.[0];
  const tenantRoleNames = tenant?.roleNames ?? [];
  const userRoleNames = descopeUser.roleNames ?? [];
  const onTenant = tenantRoleNames.includes(AGENCY_PRINCIPAL_ROLE);
  const onUser = userRoleNames.includes(AGENCY_PRINCIPAL_ROLE);
  if (shouldHavePrincipal === (onTenant || onUser)) {
    return { changed: false };
  }

  const fail = (action: string, res: DescopeResponse): never => {
    throw new Error(`Failed to ${action} the "${AGENCY_PRINCIPAL_ROLE}" role in Descope: ${JSON.stringify(res.error)}`);
  };

  if (shouldHavePrincipal) {
    await ensureAgencyPrincipalRoleExists(descopeClient);
    const useTenant = Boolean(tenant?.tenantId) && (tenantRoleNames.length > 0 || userRoleNames.length === 0);
    const res = useTenant
      ? await descopeClient.management.user.addTenantRoles(loginId, tenant!.tenantId!, [AGENCY_PRINCIPAL_ROLE])
      : await descopeClient.management.user.addRoles(loginId, [AGENCY_PRINCIPAL_ROLE]);
    if (!res.ok) {
      fail('add', res);
    }
    return { changed: true, level: useTenant ? 'tenant' : 'user' };
  }

  if (onTenant) {
    const res = await descopeClient.management.user.removeTenantRoles(loginId, tenant!.tenantId!, [
      AGENCY_PRINCIPAL_ROLE
    ]);
    if (!res.ok) {
      fail('remove', res);
    }
  }
  if (onUser) {
    const res = await descopeClient.management.user.removeRoles(loginId, [AGENCY_PRINCIPAL_ROLE]);
    if (!res.ok) {
      fail('remove', res);
    }
  }
  return { changed: true, level: onTenant ? 'tenant' : 'user' };
}

// Descope custom attribute → the Cognito claim the Descope IdP maps it to (staff-appsync-be
// template.yaml AttributeMapping). These attributes are writable by the staff app client (the IdP
// mapping requires it), so a signed-in agent can rewrite their own with UpdateUserAttributes. The
// staffuserpermissions record carries Descope's values and StaffPreTokenGenerationLambda stamps them
// onto every token instead (#36196). Every writer of that record must include them, or the override
// silently stops for that user until their next login.
const AGENCY_CLAIM_SOURCES: Record<string, string> = {
  'custom:affinity_groups': 'affinityGroups',
  'custom:affinity_group_regex': 'affinityGroupRegex',
  'custom:affinity_code': 'affinityCode',
  'custom:allowed_states': 'allowedStates',
  'custom:unlicensed_states': 'unlicensedStates'
};
// Descope decides whether someone is an agent: no affinity there means none, even when Cognito holds a
// stale or self-written value. For an agent, a missing state list defaults to [] too, so a self-written
// Cognito value can't widen their licensed states. Internal staff's other claims are only overridden when
// Descope has them, so a legacy user's states aren't stripped by an attribute Descope never carried.
const AGENCY_LIST_DEFAULTS: Record<string, string> = {
  'custom:affinity_groups': '[]',
  'custom:affinity_group_regex': '[]'
};
const AGENT_STATE_LIST_DEFAULTS: Record<string, string> = {
  'custom:allowed_states': '[]',
  'custom:unlicensed_states': '[]'
};

function parseStringArray(raw: string | undefined): string[] | undefined {
  try {
    const value = JSON.parse(raw ?? '');
    return Array.isArray(value) && value.every((entry) => typeof entry === 'string') ? value : undefined;
  } catch {
    return undefined;
  }
}

// The staff-users tools prune an agent's codes that a stem already covers, but only when the list is too
// long for Cognito's 2048-character attribute, using trimmed, upper-cased, de-duplicated stems
// (create-staff-user / update-staff-user). Descope keeps the full list: a GU0001-stem agent has 444 codes
// (~6 KB) there. Stamping that onto every ID token would bloat the Authorization header and differ from
// what the token carried before #36196, so apply the same rule, emitting the same canonical stems. A short
// list keeps its codes and stems as-is (getAccount allows a code only by exact code or exact stem). A list that doesn't parse is left as-is; consumers
// fail closed on it.
const COGNITO_ATTRIBUTE_MAX_LENGTH = 2048;

function dropCodesCoveredByStems(claims: Record<string, string>): void {
  const serializedCodes = claims['custom:affinity_groups'];
  if (!serializedCodes || serializedCodes.length <= COGNITO_ATTRIBUTE_MAX_LENGTH) {
    return;
  }
  const codes = parseStringArray(serializedCodes);
  const stems = [
    ...new Set(
      (parseStringArray(claims['custom:affinity_group_regex']) ?? [])
        .map((stem) => stem.trim().toUpperCase())
        .filter(Boolean)
    )
  ];
  if (!codes || !stems.length) {
    return;
  }
  claims['custom:affinity_groups'] = JSON.stringify(
    codes.filter((code) => stems.every((stem) => !code.startsWith(stem)))
  );
  // Emit the stems that did the pruning, as the tools store them: getAccount matches stems by exact
  // equality, so a raw ' gu0001 ' left in the claim would deny the GU0001_* accounts just pruned away.
  claims['custom:affinity_group_regex'] = JSON.stringify(stems);
}

/** The agency claims to stamp on a staff user's tokens, from their Descope custom attributes (JSON strings). */
export function buildAgencyClaims(customAttributes: Record<string, unknown> | undefined): Record<string, string> {
  const claims: Record<string, string> = {};
  for (const [claimName, attributeName] of Object.entries(AGENCY_CLAIM_SOURCES)) {
    const value = customAttributes?.[attributeName];
    if (typeof value === 'string' && value !== '') {
      claims[claimName] = value;
    } else if (Array.isArray(value)) {
      // Some tool paths store a bare [] rather than its JSON string; claims are always JSON strings.
      claims[claimName] = JSON.stringify(value);
    } else if (AGENCY_LIST_DEFAULTS[claimName]) {
      claims[claimName] = AGENCY_LIST_DEFAULTS[claimName];
    }
  }
  dropCodesCoveredByStems(claims);
  const isAgent = ['custom:affinity_groups', 'custom:affinity_group_regex'].some(
    (name) => claims[name] !== undefined && claims[name] !== '[]'
  );
  if (isAgent) {
    for (const [claimName, value] of Object.entries(AGENT_STATE_LIST_DEFAULTS)) {
      claims[claimName] ??= value;
    }
  }
  return claims;
}
